/**
 * Multi-select (check_list) answers for Variant B.
 * Figma Weight-Loss 5313:116632 — full-width rounded rows, label left,
 * orange filled checkbox + white check when selected; gray empty box when not.
 */

import React from "react";
import { orderAnswerOptionsChildren } from "../components/helpers";
import { useShellIdFn } from "./shellContext";

interface MultiSelectAnswersProps {
  answersGroup: any;
  answersSelectedMap: Record<string | number, any>;
  onToggle: (child: any) => void;
  errorMode?: boolean;
}

const CheckIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2.5 6.2L4.8 8.5L9.5 3.5"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MultiSelectAnswers: React.FC<MultiSelectAnswersProps> = ({
  answersGroup,
  answersSelectedMap,
  onToggle,
  errorMode,
}) => {
  // Both shells render this list, so scope the ids to keep them unique.
  const scopeId = useShellIdFn();

  const children = orderAnswerOptionsChildren(
    answersGroup?.children || [],
    answersGroup?.recipe?.recipe_question_id
  );

  const anySelected = children.some((c: any) => !!answersSelectedMap[c.recipe.id]);

  return (
    <div id={scopeId("wl2_multi_select_answers")} className="flex flex-col gap-3 w-full">
      {children.map((child: any) => {
        const selected = !!answersSelectedMap[child.recipe.id];
        const invalid = !!errorMode && !anySelected;
        // checkbox_option stores the option text in `value`; `label` carries the
        // parent check_list heading (see legacy CheckBoxList), so never show it here.
        const label =
          child.recipe.value ||
          child.recipe.long_label ||
          "";

        return (
          <button
            key={child.recipe.id}
            id={scopeId(`answer_${child.recipe.id}`)}
            type="button"
            onClick={() => onToggle(child)}
            className="w-full box-border rounded-[14px] px-[22px] py-[14px] flex items-center justify-between gap-3 text-left cursor-pointer transition-transform duration-100 active:scale-[0.99] hover:shadow-sm bg-white"
            style={{
              border: `1px solid ${
                selected ? "#f59402" : invalid ? "var(--primary-danger)" : "#dee3ed"
              }`,
              backgroundColor: selected ? "#fffaf3" : "#ffffff",
            }}
          >
            <span
              className={`font-['Roboto'] font-normal text-[16px] leading-[23px] flex-1 min-w-0 ${
                invalid && !selected ? "text-[var(--primary-danger)]" : "text-[#08080a]"
              }`}
              dangerouslySetInnerHTML={{ __html: String(label) }}
            />
            <span
              className="shrink-0 size-5 rounded-[2px] flex items-center justify-center box-border"
              style={
                selected
                  ? { backgroundColor: "#f69503", border: "none" }
                  : {
                      backgroundColor: "transparent",
                      border: `1px solid ${invalid ? "var(--primary-danger)" : "#b5bdcc"}`,
                    }
              }
              aria-hidden
            >
              {selected ? <CheckIcon /> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default MultiSelectAnswers;
